import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { postEvent } from '@/services/events';
import { EventType } from '@/services/events/types';

const TRACKER_EVENTS = [
  'click',
  'keydown',
  'input',
  'paste',
  'touchstart',
  'wheel',
] as const;

const THROTTLED_EVENTS = ['wheel'];
const WINDOW_EVENTS = ['focus', 'blur'] as const;
const DOCUMENT_EVENTS = ['visibilitychange'] as const;

const THROTTLE_MS = 1_000;
const INTERVAL_MS = 60_000; // Event request interval
const INACTIVITY_TIMEOUT_MS = 30 * 60_000; // Inactivity timeout
const EVENT_DURATION = 60;

export function useUserActivityTracker() {
  const { pathname } = useLocation();
  const moduleName = pathname.split('/').pop() || '/';

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastThrottleRef = useRef(0);

  const stopEventTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const clearInactivityTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const sendEvent = useCallback(() => {
    void postEvent({
      duration: EVENT_DURATION,
      eventType: EventType.ActiveStatus,
      moduleName,
    });
  }, [moduleName]);

  const startEventTracking = useCallback(
    (immediate = false) => {
      if (intervalRef.current) return;

      if (immediate) sendEvent();

      intervalRef.current = setInterval(sendEvent, INTERVAL_MS);
    },
    [sendEvent]
  );

  const resetInactivityTimeout = useCallback(() => {
    clearInactivityTimeout();
    timeoutRef.current = setTimeout(() => {
      stopEventTracking();
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  const isTabActive = () =>
    document.visibilityState === 'visible' && document.hasFocus();

  useEffect(() => {
    const onInteraction = (eventType: string) => {
      const now = Date.now();

      if (THROTTLED_EVENTS.includes(eventType)) {
        if (now - lastThrottleRef.current < THROTTLE_MS) return;
        lastThrottleRef.current = now;
      }

      if (isTabActive()) {
        startEventTracking(true);
        resetInactivityTimeout();
      }
    };

    const handlers = TRACKER_EVENTS.map((eventType) => {
      const handler = () => onInteraction(eventType);
      window.addEventListener(eventType, handler, { passive: true });
      return { eventType, handler };
    });

    const onFocus = () => {
      if (isTabActive()) {
        startEventTracking(false);
        resetInactivityTimeout();
      }
    };

    const onBlurOrHide = () => {
      stopEventTracking();
      clearInactivityTimeout();
    };

    WINDOW_EVENTS.forEach((eventType) => {
      const handler = eventType === 'focus' ? onFocus : onBlurOrHide;
      window.addEventListener(eventType, handler);
    });

    DOCUMENT_EVENTS.forEach((eventType) => {
      document.addEventListener(eventType, onBlurOrHide);
    });

    return () => {
      handlers.forEach(({ eventType, handler }) =>
        window.removeEventListener(eventType, handler)
      );
      WINDOW_EVENTS.forEach((eventType) => {
        const handler = eventType === 'focus' ? onFocus : onBlurOrHide;
        window.removeEventListener(eventType, handler);
      });
      DOCUMENT_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, onBlurOrHide);
      });
      stopEventTracking();
      clearInactivityTimeout();
    };
  }, [moduleName, startEventTracking, resetInactivityTimeout]);
}
